import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { DataService } from '../shared/data.service';
import { concatMap, of, forkJoin, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { CarrierWithPayerAndState } from '../Models/CarrierWithPayerAndState';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { DeleteMappingComponent } from './delete-mapping/delete-mapping.component';

@Component({
    selector: 'app-payer-mapping',
    templateUrl: './payer-mapping.component.html',
    styleUrls: ['./payer-mapping.component.css'],
    standalone: false
})
export class PayerMappingComponent implements AfterViewInit {
  errorMessage = '';
  carriers: MatTableDataSource<CarrierWithPayerAndState> = new MatTableDataSource<CarrierWithPayerAndState>();
  totalCount: number = 0;
  pageSize: number = 500;
  displayedColumns: string[] = ['select', 'carrierName', 'payerState', 'payerName'];
  selection = new SelectionModel<CarrierWithPayerAndState>(true, []);
  filter: string = '';
  isAddButtonClicked: boolean = false;
  isLoadingPayers: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private dataSvc: DataService,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.getAllCarriers();
  }

  ngAfterViewInit() {
    this.carriers.paginator = this.paginator;
    this.carriers.sort = this.sort;
  }

  ngOnChanges() {
  }

  resetAddButton() {
    this.isAddButtonClicked = false;
  }

  addButtonClicked() {
    this.isAddButtonClicked = true;
  }

  isAddButtonDisabled() {
    const noSelection = this.selection.selected.length === 0;
    const hasNational = this.selection.selected.some((row: CarrierWithPayerAndState) => row.payerState === null);
    const allPayerStatesMapped = !hasNational && !noSelection ? this.checkIfAllPayerStatesMapped() : false;
    const hasMultipleSameCarriers = this.checkIfMultipleSameCarriersAreSelected();
    return noSelection || hasNational || allPayerStatesMapped || hasMultipleSameCarriers;
  }

  isEditButtonDisabled() {
    const noSelection = this.selection.selected.length === 0;
    const hasMultipleSameCarriers = this.checkIfMultipleSameCarriersAreSelected();
    return noSelection || hasMultipleSameCarriers;
  }

  isDeleteButtonDisabled() {
    const noSelection = this.selection.selected.length === 0;
    const noPayer = this.selection.selected.some((row: CarrierWithPayerAndState) => row.payerName === null);
    return noSelection || noPayer;
  }

  checkIfMultipleSameCarriersAreSelected() {
    const selectedCarriers = this.selection.selected.map((row: CarrierWithPayerAndState) => row.carrierName);
    const uniqueCarriers = new Set(selectedCarriers);
    return selectedCarriers.length !== uniqueCarriers.size;
  }

  checkIfAllPayerStatesMapped() {
    let filteredCarriers: CarrierWithPayerAndState[][] = [];
    for (let i = 0; i < this.selection.selected.length; i++) {
      filteredCarriers.push(
        this.carriers.data.filter(
          (carrier: CarrierWithPayerAndState) =>
            carrier.carrierName === this.selection.selected[i].carrierName
        )
      );
    }
    const states = ['CA','IL', 'AZ', 'GA'];
    const resultsArray: boolean[] = [];
    // Check if every subarray contains all states
    filteredCarriers.forEach((subArray: CarrierWithPayerAndState[]) => {
      const payerStates = subArray.map((carrier: CarrierWithPayerAndState) => carrier.payerState);
      let result = states.sort().join(',') === payerStates.sort().join(',') ? true : false;
      resultsArray.push(result);
    });
    const hasAllPayerStatesMapped = resultsArray.some((result: boolean) => result === true);
    return hasAllPayerStatesMapped;
  }

  openDialog() {
    const dialogRef = this.dialog.open(DeleteMappingComponent, {
      data: this.selection.selected
    });

    dialogRef.afterClosed().subscribe(result => {
      this.refreshCarrierTable();
    });
  }

  refreshCarrierTable() {
    this.clearFilter();
    this.selection.clear();
    this.getAllCarriers();
    this.carriers.paginator = this.paginator;
    this.carriers.sort = this.sort;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.carriers.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.carriers.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: CarrierWithPayerAndState): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.carrierName}`;
  }

  onSelectRow(event: MatCheckboxChange, row: CarrierWithPayerAndState) {
    this.selection.toggle(row);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.carriers.filter = filterValue.trim().toLowerCase();

    if (this.carriers.paginator) {
      this.carriers.paginator.firstPage();
    }
  }

  clearFilter() {
    this.carriers.filter = '';
    this.filter = '';
  }

  hasResults(source: any) {
    return source.hasOwnProperty("results");
  }

  getAllCarriers() {
    this.carriers = new MatTableDataSource<CarrierWithPayerAndState>(); // Reset carriers array
    this.errorMessage = ''; // Reset error message
    this.isLoadingPayers = true;
    // Get first page to determine total count
    this.dataSvc.getCarriers(1, this.pageSize)
      .pipe(
        concatMap((response: any) => {
          if (this.hasResults(response)) {
            this.carriers.data = [...response.results];
            this.totalCount = response.totalCount;

            // Calculate remaining pages
            const totalPages = Math.ceil(this.totalCount / this.pageSize);
            const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

            // If there are more pages, request them in parallel
            if (remainingPages.length > 0) {
              return forkJoin(
                remainingPages.map(page => this.dataSvc.getCarriers(page, this.pageSize))
              ).pipe(
                map(responses => {
                  responses.forEach((res: any) => {
                    if (this.hasResults(res)) {
                      this.carriers.data = [...this.carriers.data, ...res.results];
                    }
                    this.carriers._updatePaginator(this.totalCount);
                  });
                })
              );
            }
          }
          return of(null);
        }),
        finalize(() => {
          this.isLoadingPayers = false;
        })
      )
      .subscribe();
  }
}
